import React, { useMemo, useState } from 'react';
import {
  Box, Button, Checkbox, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tooltip, Typography, Stack, CircularProgress,
  TextField, MenuItem, Autocomplete,
  Dialog, DialogContent, DialogTitle, IconButton, Menu, Alert
} from '@mui/material';
import { TABLE_SX, TH_SX, TD_SX } from '../../common/TableDesign';
import TablePagination from '../../common/TablePagination';
import {
  GetApp as ExcelIcon, PictureAsPdf as PdfIcon, Close as CloseIcon,
  ArrowDropDown as ArrowDropDownIcon, Download as DownloadIcon
} from '@mui/icons-material';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { exportReport } from './excelExport';
import { buildPivotData, describeAssessmentScope, extractSubjects, studentName } from './hubUtils';
import ClassMarkListPdf from './ClassMarkListPdf';
import { BulkStudentPdfDoc } from './StudentAllAssessmentsPdf';

const EXPORT_TYPES = [
  { key: 'marks',       label: 'Class Mark List',       desc: 'All students × subjects — raw marks, aggregate, division, position' },
  { key: 'performance', label: 'Performance Report',    desc: 'Top / Middle / Bottom bands with student details' },
  { key: 'subject_avg', label: 'Subject Averages',      desc: 'Per-subject average, highest, lowest, pass rate' },
  { key: 'assessment',  label: 'Per-Assessment Report', desc: 'One sheet per assessment type (BOT, MOT, EOT…)' },
  { key: 'student',     label: 'Student Summary',       desc: 'One row per student — all assessments, aggregate, division, position' },
];

const ClassMarkListTable = ({ studentMarks, assessmentConfigs, students, filters, classes, terms, school, gradingBoundaries, onPrintStudent }) => {
  const [exporting, setExporting] = useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedSubjectIds, setSelectedSubjectIds] = useState([]);
  const [showClassPdf, setShowClassPdf] = useState(false);
  const [selectedStudentIds, setSelectedStudentIds] = useState(new Set());
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const allSubjects = useMemo(() => extractSubjects(studentMarks), [studentMarks]);
  const assessmentScope = useMemo(
    () => describeAssessmentScope(assessmentConfigs, filters.assessment_type_ids || []),
    [assessmentConfigs, filters.assessment_type_ids]
  );

  const { subjects, rows, assessmentCols, isFiltered, isSingleType } = useMemo(
    () => buildPivotData(
      studentMarks, assessmentConfigs,
      selectedSubjectIds.length ? selectedSubjectIds : null,
      gradingBoundaries,
      filters.assessment_type_ids || []
    ),
    [studentMarks, assessmentConfigs, selectedSubjectIds, gradingBoundaries, filters.assessment_type_ids]
  );

  const filteredRows = useMemo(() => {
    let r = rows;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      r = r.filter((row) =>
        studentName(row.student).toLowerCase().includes(q) ||
        (row.student?.registration_no || '').toLowerCase().includes(q)
      );
    }
    if (filters.student_id) r = r.filter((row) => String(row.student?.id) === String(filters.student_id));
    if (filters.gender)     r = r.filter((row) => row.student?.gender === filters.gender);
    if (filters.min_percentage !== '' && filters.min_percentage !== undefined)
      r = r.filter((row) => row.overall_percentage >= parseFloat(filters.min_percentage));
    if (filters.max_percentage !== '' && filters.max_percentage !== undefined)
      r = r.filter((row) => row.overall_percentage <= parseFloat(filters.max_percentage));
    return r;
  }, [rows, search, filters]);

  // ── Selection ──────────────────────────────────────────────────────────────
  const allFilteredIds = useMemo(() => filteredRows.map((r) => r.student.id), [filteredRows]);
  const allSelected  = allFilteredIds.length > 0 && allFilteredIds.every((id) => selectedStudentIds.has(id));
  const someSelected = allFilteredIds.some((id) => selectedStudentIds.has(id)) && !allSelected;

  const toggleAll = () => setSelectedStudentIds((prev) => {
    const next = new Set(prev);
    if (allSelected) allFilteredIds.forEach((id) => next.delete(id));
    else             allFilteredIds.forEach((id) => next.add(id));
    return next;
  });

  const toggleOne = (id) => setSelectedStudentIds((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  // Report cards for selected students
  const bulkReportCards = useMemo(
    () => studentMarks.filter((rc) => selectedStudentIds.has(String(rc.student_id))),
    [studentMarks, selectedStudentIds]
  );

  const classLabel = useMemo(() => {
    const cls  = classes.find((c) => String(c.id) === String(filters.class_id));
    const term = terms.find((t) => String(t.id) === String(filters.term_id));
    return [cls?.name || 'All Classes', term?.name, filters.academic_year].filter(Boolean).join(' · ');
  }, [classes, terms, filters]);

  const handleExport = async (type) => {
    setExportMenuAnchor(null);
    setExporting(true);
    try {
      const result = exportReport(type, filteredRows, subjects, assessmentCols, classLabel, filters, assessmentScope);
      if (!result.success) alert(`Export error: ${result.message}`);
    } catch (err) { alert(`Export failed: ${err.message}`); }
    finally { setExporting(false); }
  };

  const showPosition = !!filters.class_id;
  const showAggDiv = isSingleType && selectedSubjectIds.length === 0;
  const totalCols = 4 + subjects.length * assessmentCols.length + 1 + (showAggDiv ? 2 : 0) + (showPosition && showAggDiv ? 1 : 0) + 1;

  const thSx = { ...TH_SX, whiteSpace: 'nowrap', fontSize: '0.85rem', py: 1.5 };
  const tdSx  = { ...TD_SX, fontSize: '0.9rem',  py: 1.5 };

  // Reset to page 1 when filters change
  const paginatedRows = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  const pagination = {
    current_page: page,
    last_page: Math.max(1, Math.ceil(filteredRows.length / rowsPerPage)),
    per_page: rowsPerPage,
    total: filteredRows.length,
  };

  return (
    <Paper sx={{ overflow: 'hidden' }}>
      {/* ── Header ── */}
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1} mb={1}>
          <Box>
            <Typography variant="h6" fontWeight={700}>Mark List</Typography>
            <Typography variant="body2" color="textSecondary">
              {classLabel} · <strong>{filteredRows.length}</strong> students · <strong>{subjects.length}</strong> subjects · <strong>{assessmentCols.length}</strong> assessments
              {filteredRows.length !== rows.length && ` (filtered from ${rows.length})`}
            </Typography>

          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
            {/* Bulk PDF download */}
            {selectedStudentIds.size > 0 && (
              <PDFDownloadLink
                document={
                  <BulkStudentPdfDoc
                    reportCards={bulkReportCards}
                    assessmentConfigs={assessmentConfigs}
                    filters={filters}
                    classes={classes}
                    terms={terms}
                    school={school}
                    gradingBoundaries={gradingBoundaries}
                    students={students}
                  />
                }
                fileName={`student-reports-${selectedStudentIds.size}-students-${new Date().toISOString().split('T')[0]}.pdf`}
              >
                {({ loading }) => (
                  <Button
                    size="small" variant="contained" color="primary"
                    startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}
                    disabled={loading}
                  >
                    {loading ? 'Generating…' : `Download ${selectedStudentIds.size} Student PDF${selectedStudentIds.size > 1 ? 's' : ''}`}
                  </Button>
                )}
              </PDFDownloadLink>
            )}

            {/* Export dropdown */}
            <Button
              size="small" variant="outlined"
              startIcon={exporting ? <CircularProgress size={14} /> : <ExcelIcon />}
              endIcon={<ArrowDropDownIcon />}
              onClick={(e) => setExportMenuAnchor(e.currentTarget)}
              disabled={exporting || !filteredRows.length}
            >
              Export
            </Button>
            <Menu anchorEl={exportMenuAnchor} open={Boolean(exportMenuAnchor)} onClose={() => setExportMenuAnchor(null)}>
              {EXPORT_TYPES.map((et) => (
                <MenuItem key={et.key} onClick={() => handleExport(et.key)}
                  sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 1 }}>
                  <Typography variant="body2" fontWeight={600}>{et.label}</Typography>
                  <Typography variant="caption" color="textSecondary">{et.desc}</Typography>
                </MenuItem>
              ))}
            </Menu>

            <Tooltip title="Class mark list PDF preview">
              <Button size="small" variant="outlined" startIcon={<PdfIcon />}
                onClick={() => setShowClassPdf(true)} disabled={!filteredRows.length}>
                Class PDF
              </Button>
            </Tooltip>
          </Stack>
        </Box>

        {/* Selection info */}
        {selectedStudentIds.size > 0 && (
          <Alert severity="info" sx={{ mb: 1, py: 0.5 }}
            action={<Button size="small" onClick={() => setSelectedStudentIds(new Set())}>Clear</Button>}>
            {selectedStudentIds.size} student{selectedStudentIds.size > 1 ? 's' : ''} selected — download their individual assessment PDFs in one file
          </Alert>
        )}

        {/* Filters */}
        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
          <TextField size="small" placeholder="Search name / reg no…" value={search}
            onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 200 }} />
          <Autocomplete multiple size="small" options={allSubjects}
            value={allSubjects.filter((s) => selectedSubjectIds.includes(String(s.id)))}
            onChange={(_, v) => setSelectedSubjectIds(v.map((s) => String(s.id)))}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
            renderInput={(p) => <TextField {...p} label="Subjects" placeholder="All" />}
            sx={{ minWidth: 240 }} limitTags={2} />
          {(search || selectedSubjectIds.length > 0) && (
            <Button size="small" onClick={() => { setSearch(''); setSelectedSubjectIds([]); }}>Clear</Button>
          )}
        </Stack>


      </Box>

      {/* ── Table ── */}
      <TableContainer component={Paper} elevation={0} sx={{ ...TABLE_SX, maxHeight: 'calc(100vh - 360px)' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {/* Checkbox col */}
              <TableCell rowSpan={2} padding="checkbox" sx={{ ...thSx, bgcolor: 'grey.50' }}>
                <Checkbox size="small" checked={allSelected} indeterminate={someSelected} onChange={toggleAll} />
              </TableCell>
              <TableCell rowSpan={2} sx={{ ...thSx, minWidth: 36 }}>#</TableCell>
              <TableCell rowSpan={2} sx={{ ...thSx, minWidth: 150 }}>Student</TableCell>
              <TableCell rowSpan={2} sx={{ ...thSx, minWidth: 90 }} align="center">Reg No</TableCell>
              {subjects.map((subj) => (
                <TableCell key={subj.id} colSpan={assessmentCols.length} align="center"
                  sx={{ ...thSx, bgcolor: '#e8f5e9', borderLeft: '1px solid #c8e6c9' }}>
                  <Tooltip title={subj.name}><span>{subj.code || subj.name}</span></Tooltip>
                </TableCell>
              ))}
              <TableCell rowSpan={2} align="center" sx={{ ...thSx, minWidth: 55, bgcolor: '#fff9c4' }}>Total</TableCell>
              {showAggDiv && <TableCell rowSpan={2} align="center" sx={{ ...thSx, minWidth: 45, bgcolor: '#f3e5f5' }}>Agg</TableCell>}
              {showAggDiv && <TableCell rowSpan={2} align="center" sx={{ ...thSx, minWidth: 90, bgcolor: '#f3e5f5' }}>Div</TableCell>}
              {showPosition && showAggDiv && <TableCell rowSpan={2} align="center" sx={{ ...thSx, minWidth: 90, bgcolor: '#e3f2fd' }}>Position</TableCell>}
              <TableCell rowSpan={2} align="center" sx={{ ...thSx, minWidth: 60 }}>PDF</TableCell>
            </TableRow>
            <TableRow>
              {subjects.map((subj) =>
                assessmentCols.map((col) => (
                  <TableCell key={`${subj.id}-${col.code}`} align="center"
                    sx={{ ...thSx, minWidth: 50, bgcolor: '#f1f8e9', borderLeft: '1px solid #ddd', fontSize: '0.75rem' }}>
                    <Tooltip title={`${col.code} (max: ${col.max_marks})`}><span>{col.code}</span></Tooltip>
                  </TableCell>
                ))
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedRows.map((row, idx) => {
              const globalIdx = (page - 1) * rowsPerPage + idx;
              const isSelected = selectedStudentIds.has(row.student.id);
              return (
                <TableRow key={row.student.id} hover selected={isSelected}
                  sx={{ bgcolor: isSelected ? '#e8f5e9' : globalIdx % 2 === 0 ? '#fff' : '#fafafa' }}>
                  <TableCell padding="checkbox">
                    <Checkbox size="small" checked={isSelected} onChange={() => toggleOne(row.student.id)} />
                  </TableCell>
                  <TableCell sx={{ ...tdSx, color: '#aaa' }}>{globalIdx + 1}</TableCell>
                  <TableCell sx={{ ...tdSx, fontWeight: 500 }}>{studentName(row.student)}</TableCell>
                  <TableCell align="center" sx={tdSx}>{row.student?.registration_no || '-'}</TableCell>

                  {subjects.map((subj) =>
                    assessmentCols.map((col) => {
                      const a = (row.marksBySubject[subj.id] || {})[col.code];
                      const absent   = a?.is_absent;
                      const exempted = a?.is_exempted;
                      const raw = (!a || absent || exempted || (a.score ?? a.marks_obtained ?? a.raw_score) == null)
                        ? (absent ? 'ABS' : exempted ? 'EXM' : '-')
                        : Number(a.score ?? a.marks_obtained ?? a.raw_score).toFixed(0);
                      return (
                        <TableCell key={`${subj.id}-${col.code}`} align="center"
                          sx={{ ...tdSx, fontWeight: 600, borderLeft: '1px solid #eee' }}>
                          {raw}
                        </TableCell>
                      );
                    })
                  )}

                  <TableCell align="center" sx={{ ...tdSx, fontWeight: 700, bgcolor: '#fff9c4' }}>
                    {(row.total ?? 0).toFixed(0)}
                  </TableCell>
                  {showAggDiv && (
                    <TableCell align="center" sx={{ ...tdSx, fontWeight: 700, bgcolor: '#f3e5f5' }}>
                      {row.aggregate ?? '-'}
                    </TableCell>
                  )}
                  {showAggDiv && (
                    <TableCell align="center" sx={{ ...tdSx, fontWeight: 700, bgcolor: '#f3e5f5',
                      color: row.division === 'Division 1' ? '#1565c0' : row.division === 'Division 2' ? '#2e7d32' :
                             row.division === 'Division 3' ? '#e65100' : row.division === 'Division 4' ? '#c62828' : '#888' }}>
                      {row.division ?? '-'}
                    </TableCell>
                  )}
                  {showPosition && showAggDiv && (
                    <TableCell align="center" sx={{ ...tdSx, fontWeight: 700, bgcolor: '#e3f2fd' }}>
                      {row.class_position != null
                        ? `${row.class_position}${row.class_total_students ? `/${row.class_total_students}` : ''}`
                        : '-'}
                    </TableCell>
                  )}
                  <TableCell align="center" sx={tdSx}>
                    <Button size="small" variant="text" startIcon={<PdfIcon fontSize="small" />}
                      onClick={() => onPrintStudent(row.student)} sx={{ minWidth: 0, px: 1 }}>PDF</Button>
                  </TableCell>
                </TableRow>
              );
            })}

            {filteredRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={totalCols} align="center" sx={{ py: 8 }}>
                  <Typography color="text.secondary">No students match the current filters</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>


        </Table>
      </TableContainer>

      <Box sx={{ px: 2, pb: 1 }}>
        <TablePagination
          pagination={pagination}
          onPageChange={(p) => setPage(p)}
          onPageSizeChange={(size) => { setRowsPerPage(Number(size)); setPage(1); }}
        />
      </Box>
      <Box sx={{ px: 2, pb: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          Check rows to select students → bulk download all their PDFs in one file · Raw marks · Aggregate & Division from grading boundaries
        </Typography>
      </Box>

      {/* Class PDF Dialog */}
      <Dialog open={showClassPdf} onClose={() => setShowClassPdf(false)} maxWidth="xl" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          Class Mark List — PDF
          <IconButton size="small" onClick={() => setShowClassPdf(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <ClassMarkListPdf
            filteredRows={filteredRows}
            subjects={subjects}
            assessmentCols={assessmentCols}
            classLabel={classLabel}
            termLabel={filters.academic_year || ''}
            school={school}
            showAggDiv={showAggDiv}
            showPosition={showPosition}
          />
        </DialogContent>
      </Dialog>
    </Paper>
  );
};

export default ClassMarkListTable;
